import { useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { FormControl, FormItem, FormLabel } from '@/components/ui/form.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command.tsx';
import { CaretSortIcon } from '@radix-ui/react-icons';
import { cn } from '@/utils/utils.ts';
import { useTranslation } from 'react-i18next';
import { FlatDeviceEntry } from '@/features/devices/components/device-tree-table/device-tree-table.utils.ts';

const ROOT_PATH = '__root__';

export const DeviceEditMoveTabForm = ({
  parentOptions,
  selectedParentPath,
  onParentPathChange,
}: {
  parentOptions: FlatDeviceEntry[];
  selectedParentPath: string;
  onParentPathChange: (path: string) => void;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { t } = useTranslation();

  const displayLabel =
    selectedParentPath === ROOT_PATH
      ? t('devices.actions.rootDevice')
      : (() => {
          const entry = parentOptions.find((e) => e.path.join('.') === selectedParentPath);
          return entry
            ? `${entry.device.name} (${entry.device.sn})`
            : t('devices.actions.rootDevice');
        })();

  return (
    <div className="flex flex-col gap-2">
      <FormItem>
        <FormLabel>{t('devices.actions.parentDevice')}</FormLabel>
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={true}>
          <PopoverTrigger asChild>
            <FormControl>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={isPopoverOpen}
                className="w-full max-w-[462px] justify-between px-3 py-2 text-sm font-normal"
              >
                <div title={displayLabel} className="truncate overflow-hidden">
                  {displayLabel}
                </div>
                <CaretSortIcon className="min-h-4 min-w-4 opacity-50" />
              </Button>
            </FormControl>
          </PopoverTrigger>
          <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
            <Command>
              <CommandInput placeholder={t('devices.actions.searchDevice')} className="h-9" />
              <CommandList>
                <CommandEmpty>{t('devices.actions.noDeviceFound')}</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value={ROOT_PATH}
                    onSelect={() => {
                      onParentPathChange(ROOT_PATH);
                      setIsPopoverOpen(false);
                    }}
                  >
                    {t('devices.actions.rootDevice')}
                    <Check
                      className={cn(
                        'ml-auto',
                        selectedParentPath === ROOT_PATH ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                  {parentOptions.map((entry) => {
                    const pathKey = entry.path.join('.');
                    return (
                      <CommandItem
                        key={pathKey}
                        value={`${entry.device.name} ${entry.device.sn}`}
                        onSelect={() => {
                          onParentPathChange(pathKey);
                          setIsPopoverOpen(false);
                        }}
                      >
                        <span style={{ paddingLeft: `${entry.depth * 0.75}rem` }}>
                          {entry.device.name}{' '}
                          <span className="text-muted-foreground">({entry.device.sn})</span>
                        </span>
                        <Check
                          className={cn(
                            'ml-auto',
                            selectedParentPath === pathKey ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </FormItem>
    </div>
  );
};
